import React, { useEffect, useState } from "react";
import { Button, Col, Collapse, DatePicker, Form, Icon, Row } from "antd";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import SelectInput from "../CommonFormItems/SelectInput/SelectInput";
import {
  buildDateRangeFromSearchParams,
  disabledRequestHistoryDate,
  getClientOptions,
  getContractOptions,
  getLabellerOptions,
  hasRequiredContractFields,
  resetDependentFields,
} from "../../../utils/materialRequestUtils";

import {
  selectMaterialRequestHistorySearchParams,
  setMaterialRequestHistoryPagination,
  setMaterialRequestHistorySearchParams,
} from "../../../redux/slices/materialRequestHistorySlice";

const { Panel } = Collapse;
const { RangePicker } = DatePicker;

const DEFAULT_DATE_RANGE = [moment().subtract(1, "month"), moment()];

function resetFields(fields, setter, setFieldsValue) {
  const resetValues = {};
  const resetOptions = {};

  fields.forEach((field) => {
    resetValues[field] = undefined;
    resetOptions[`${field}Options`] = [];
  });

  setter((prev) => ({
    ...prev,
    ...resetOptions,
  }));

  setFieldsValue(resetValues);
}

function getMomentDate(value, fallback) {
  return value ? moment(value) : fallback;
}

function RequestHistorySearchForm({
  form,
  reduxUserProfiles,
  fetchRequestHistory,
  requestHistory,
}) {
  const { getFieldDecorator, getFieldValue, setFieldsValue } = form;

  const dispatch = useDispatch();
  const searchParams = useSelector(selectMaterialRequestHistorySearchParams);

  const [isSearchDisabled, setIsSearchDisabled] = useState(true);

  const [contractDetails, setContractDetails] = useState({
    contractNumberOptions: [],
    clientNumberOptions: [],
    labellerCodeOptions: [],
  });

  const hasValidDateRange = () => {
    const dateRange = getFieldValue("dateRange");

    return (
      Array.isArray(dateRange) &&
      dateRange.length === 2 &&
      dateRange[0] &&
      dateRange[1]
    );
  };

  const updateSearchStatus = () => {
    setTimeout(() => {
      const hasRequiredFields = hasRequiredContractFields({
        contractNumber: getFieldValue("contractNumber"),
        clientNumber: getFieldValue("clientNumber"),
        labellerCode: getFieldValue("labellerCode"),
      });

      setIsSearchDisabled(!hasRequiredFields || !hasValidDateRange());
    }, 0);
  };

  const applyContractOptions = (contractNumber) => {
    const clientOptions = getClientOptions(reduxUserProfiles, contractNumber);

    setContractDetails((prev) => ({
      ...prev,
      clientNumberOptions: clientOptions,
    }));

    return clientOptions;
  };

  const applyClientOptions = (clientNumber) => {
    const labellerOptions = getLabellerOptions(reduxUserProfiles, clientNumber);

    setContractDetails((prev) => ({
      ...prev,
      labellerCodeOptions: labellerOptions,
    }));

    return labellerOptions;
  };

  const handleContractChange = (value) => {
    resetFields(
      ["clientNumber", "labellerCode"],
      setContractDetails,
      setFieldsValue,
    );

    setIsSearchDisabled(true);

    if (!value) return;

    const clientOptions = applyContractOptions(value);

    if (clientOptions.length === 1) {
      const [onlyClient] = clientOptions;

      setFieldsValue({
        clientNumber: onlyClient,
      });

      handleClientChange(onlyClient, { contractNumber: value });
    }
  };

  const handleClientChange = (value, previousValues = {}) => {
    resetFields(["labellerCode"], setContractDetails, setFieldsValue);

    setIsSearchDisabled(true);

    if (!value) return;

    const labellerOptions = applyClientOptions(value);

    if (labellerOptions.length === 1) {
      const [onlyLabeller] = labellerOptions;

      setFieldsValue({
        labellerCode: onlyLabeller,
      });

      updateSearchStatus();
    }
  };

  const handleDependencyChange = (value, name, previousValues = {}) => {
    const dependencyHandlers = {
      contractNumber: () => handleContractChange(value),
      clientNumber: () => handleClientChange(value, previousValues),
      labellerCode: updateSearchStatus,
    };

    if (dependencyHandlers[name]) {
      dependencyHandlers[name]();
    }
  };

  const disabledDate = (current) => {
    const today = moment().endOf("day");
    const oneYearAgo = moment().subtract(1, "year").startOf("day");

    return current && (current > today || current < oneYearAgo);
  };

  const buildSearchParamsFromForm = () => {
    const values = form.getFieldsValue();
    const [startDate, endDate] = values.dateRange || DEFAULT_DATE_RANGE;

    return {
      contractNumber: values.contractNumber,
      clientNumber: values.clientNumber,
      labellerCode: values.labellerCode,
      startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
      endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
    };
  };

  const handleSearch = async (event) => {
    event.preventDefault();

    if (isSearchDisabled || requestHistory?.isLoading || !hasValidDateRange()) {
      return;
    }

    const nextSearchParams = buildSearchParamsFromForm();

    dispatch(setMaterialRequestHistorySearchParams(nextSearchParams));
    dispatch(setMaterialRequestHistoryPagination({ page: 1, size: 10 }));

    await fetchRequestHistory({
      ...nextSearchParams,
      startDate: moment(nextSearchParams.startDate),
      endDate: moment(nextSearchParams.endDate),
      page: 1,
      size: 10,
    });
  };

  const applySavedSearchParams = (contractOptions) => {
    if (!searchParams?.contractNumber) return false;

    const clientOptions = getClientOptions(
      reduxUserProfiles,
      searchParams.contractNumber,
    );

    const labellerOptions = getLabellerOptions(
      reduxUserProfiles,
      searchParams.clientNumber,
    );

    setContractDetails({
      contractNumberOptions: contractOptions,
      clientNumberOptions: clientOptions,
      labellerCodeOptions: labellerOptions,
    });

    setFieldsValue({
      contractNumber: searchParams.contractNumber,
      clientNumber: searchParams.clientNumber,
      labellerCode: searchParams.labellerCode,
      dateRange: buildDateRangeFromSearchParams(searchParams),
    });

    setIsSearchDisabled(!hasRequiredContractFields(searchParams));

    return true;
  };

  const initializeContractFields = () => {
    const contractOptions = getContractOptions(reduxUserProfiles);

    setContractDetails((prev) => ({
      ...prev,
      contractNumberOptions: contractOptions,
    }));

    const restoredFromRedux = applySavedSearchParams(contractOptions);

    if (restoredFromRedux) return;

    if (contractOptions.length === 1) {
      const [onlyContract] = contractOptions;

      setFieldsValue({
        contractNumber: onlyContract,
      });

      handleDependencyChange(onlyContract, "contractNumber");
    }
  };

  useEffect(() => {
    initializeContractFields();
  }, [reduxUserProfiles, searchParams]);

  return (
    <div className="RequestHistorySearchForm">
      <Form onSubmit={handleSearch}>
        <Row className="form-section">
          <Collapse
            defaultActiveKey={["1"]}
            expandIcon={({ isActive }) => (
              <Icon type="caret-right" rotate={isActive ? 90 : 0} />
            )}
          >
            <Panel
              header="Buscador"
              key="1"
              extra={<Icon type="minus-circle" />}
              className="Busca"
            >
              <Row
                type="flex"
                justify="space-between"
                gutter={[16, 16]}
                className="pr-grid-row request-history-search-row"
              >
                <Col xs={24} md={7} className="pr-col">
                  <SelectInput
                    getFieldDecorator={getFieldDecorator}
                    label="Contrato"
                    name="contractNumber"
                    options={contractDetails.contractNumberOptions}
                    allowClear
                    disabled={contractDetails.contractNumberOptions.length <= 1}
                    callback={handleDependencyChange}
                    placeholder="Seleccione un contrato..."
                    required
                  />
                </Col>

                <Col xs={24} md={7} className="pr-col">
                  <SelectInput
                    getFieldDecorator={getFieldDecorator}
                    label="Detallable"
                    name="clientNumber"
                    options={contractDetails.clientNumberOptions}
                    allowClear
                    disabled={contractDetails.clientNumberOptions.length <= 1}
                    callback={handleDependencyChange}
                    placeholder="Seleccione un detallable..."
                    required
                  />
                </Col>

                <Col xs={24} md={7} className="pr-col">
                  <SelectInput
                    getFieldDecorator={getFieldDecorator}
                    label="Etiquetador"
                    name="labellerCode"
                    options={contractDetails.labellerCodeOptions}
                    allowClear
                    disabled={contractDetails.labellerCodeOptions.length <= 1}
                    callback={handleDependencyChange}
                    placeholder="Seleccione un etiquetador..."
                    required
                  />
                </Col>

                <Col
                  xs={24}
                  md={3}
                  className="pr-col request-history-search-button-col"
                >
                  <Form.Item className="search-actions">
                    <Button
                      htmlType="submit"
                      className={
                        !isSearchDisabled ? "submit-btn search-btn" : ""
                      }
                      disabled={isSearchDisabled}
                    >
                      Buscar
                    </Button>
                  </Form.Item>
                </Col>

                <Col
                  xs={24}
                  md={7}
                  className="pr-col request-history-date-range-col"
                >
                  <Form.Item label="Rango de fechas">
                    {getFieldDecorator("dateRange", {
                      initialValue:
                        buildDateRangeFromSearchParams(searchParams),
                      rules: [
                        {
                          required: true,
                          message: "Debe seleccionar un rango de fechas.",
                        },
                      ],
                    })(
                      <RangePicker
                        disabledDate={disabledDate}
                        format="DD/MM/YYYY"
                        allowClear
                        onChange={updateSearchStatus}
                      />,
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          </Collapse>
        </Row>
      </Form>
    </div>
  );
}

export default Form.create()(RequestHistorySearchForm);
